import { WeeklyWorkDayDto, WorkPlanDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from "react";
import moment from "moment";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { useWeeklyWorkDaysByWorkPlan } from "../../../../stores/User/user.queries";
import {
  useCreateWeeklyWorkDay,
  useUpdateWeeklyWorkDay,
  useDeleteWeeklyWorkDay,
} from "../../../../stores/User/user.mutations";
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Stack,
  alpha,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import Controls from "../../../../atoms/Controls";
import { Delete, Edit, Add, Save, Clear, ChevronRight } from "@mui/icons-material";
import FlatCard from "../../../../atoms/FlatCard";

import {
  MergedWeeklyWorkDay,
  PENDING_COLOR,
  PendingWeeklyWorkDayChange,
  createPendingId,
  formatHours,
  formatWorkDayTime,
  mergePendingWorkDays,
  workDayDurationMinutes,
} from "./pendingWorkDays";

export type { PendingWeeklyWorkDayChange };

interface WeeklyWorkDaysProps {
  user: any;
  workPlanId?: any;
  /** Whether the work plan has historical data (validityStart < today) */
  hasHistoricalData?: boolean;
  /**
   * View-only: no editing, deleting or adding of rows. Set for a plan whose
   * validity has ended — its hours are history and may not be changed at all
   * (MD-351). Only the plan's validity dates remain correctable, in the plan
   * list above.
   */
  readOnly?: boolean;
  /**
   * Changes the user has made but not yet applied. Shown here as well as in
   * the calendar and the banner: a table that keeps reporting the saved week
   * while the banner counts four changes reads as "my edit was lost".
   */
  pendingChanges?: PendingWeeklyWorkDayChange[];
  onItemClick?: (workDay: WeeklyWorkDayDto) => void;
  onItemChange?: () => void; // Optional - not needed with React Query
  /** Called when a change requires versioning (plan has historical data) */
  onVersioningRequired?: (change: PendingWeeklyWorkDayChange) => void;
}

interface FormValues {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  /** New rows can be created as a voluntary Pause (MD-238 P4). */
  isBreak?: boolean;
}

const WeeklyWorkDays: React.FC<WeeklyWorkDaysProps> = ({
  user,
  workPlanId,
  hasHistoricalData = false,
  readOnly = false,
  pendingChanges = [],
  onItemClick,
  onItemChange,
  onVersioningRequired,
}) => {
  // React Query hooks
  const { data: weeklyWorkDaysData } = useWeeklyWorkDaysByWorkPlan(user.id, workPlanId);
  const createWeeklyWorkDayMutation = useCreateWeeklyWorkDay();
  const updateWeeklyWorkDayMutation = useUpdateWeeklyWorkDay();
  const deleteWeeklyWorkDayMutation = useDeleteWeeklyWorkDay();

  const [isAddingRow, setIsAddingRow] = useState(false);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({
    dayOfWeek: 0,
    startTime: "",
    endTime: "",
    isBreak: false,
  });
  const [selectedWeeklyWorkDay, setSelectedWeeklyWorkDay] = useState<WeeklyWorkDayDto>();
  // The row the confirmation dialog acts on, held separately from the merely
  // highlighted row — see the matching comment in WorkPlans.
  const [weeklyWorkDayToDelete, setWeeklyWorkDayToDelete] = useState<WeeklyWorkDayDto | null>(null);

  // This component stays mounted while `workPlanId` changes, so row-level state
  // would otherwise survive into a work plan (or user) it does not belong to.
  useEffect(() => {
    setIsAddingRow(false);
    setEditingRowId(null);
    setFormValues({ dayOfWeek: 0, startTime: "", endTime: "", isBreak: false });
    setSelectedWeeklyWorkDay(undefined);
    setWeeklyWorkDayToDelete(null);
  }, [user?.id, workPlanId]);

  const workdayColumns = [
    { id: "id", label: "Id", minWidth: 50 },
    { id: "dayOfWeek", label: "Wochentag", minWidth: 50 },
    { id: "startTime", label: "Beginn", minWidth: 50 },
    { id: "endTime", label: "Ende", minWidth: 80 },
    { id: "type", label: "Typ", minWidth: 80 },
    {
      id: "actions",
      label: "Aktionen",
      minWidth: 50,
    },
  ];

  const daysOfWeek = [
    { label: "Montag", value: "Montag", id: "1" },
    { label: "Dienstag", value: "Dienstag", id: "2" },
    { label: "Mittwoch", value: "Mittwoch", id: "3" },
    { label: "Donnerstag", value: "Donnerstag", id: "4" },
    { label: "Freitag", value: "Freitag", id: "5" },
    { label: "Samstag", value: "Samstag", id: "6" },
    { label: "Sonntag", value: "Sonntag", id: "7" },
  ];

  /**
   * The saved week with the pending changes laid over it — the same merge the
   * calendar draws from, so the table, the calendar and the banner always
   * describe one and the same week.
   */
  const mergedWorkDays = useMemo(
    () => mergePendingWorkDays(weeklyWorkDaysData, pendingChanges),
    [weeklyWorkDaysData, pendingChanges],
  );

  const hasPendingChanges = pendingChanges.length > 0;

  // Format weekly work days times
  const weeklyWorkDays = useMemo(
    () =>
      mergedWorkDays.map((workDay) => ({
        ...workDay,
        startTime: formatWorkDayTime(workDay.startTime, "HH:mm"),
        endTime: formatWorkDayTime(workDay.endTime, "HH:mm"),
      })),
    [mergedWorkDays],
  );

  // Calculate total hours summary — over the merged week, so the totals answer
  // "what will these hours be once I apply this", not "what were they before I
  // started".
  const weeklyHoursSummary = useMemo(() => {
    let totalMinutes = 0;
    let breakMinutes = 0;

    mergedWorkDays.forEach((workDay) => {
      const durationMinutes = workDayDurationMinutes(workDay);

      if (workDay.isBreak) {
        breakMinutes += durationMinutes;
      } else {
        totalMinutes += durationMinutes;
      }
    });

    const totalHours = totalMinutes / 60;
    const breakHours = breakMinutes / 60;
    const netHours = totalHours - breakHours;

    return {
      totalHours: Math.round(totalHours * 100) / 100,
      breakHours: Math.round(breakHours * 100) / 100,
      netHours: Math.round(netHours * 100) / 100,
    };
  }, [mergedWorkDays]);

  const handleRowClick = async (workDay: WeeklyWorkDayDto) => {
    setSelectedWeeklyWorkDay(workDay);
    // console.log("workPlan clicked", workDay);
    return onItemClick && onItemClick(workDay);
  };

  const addNewRow = () => {
    setIsAddingRow(true);
    // setWeeklyWorkDays([...weeklyWorkDays, {
    //   id: "",
    //   dayOfWeek: 1,
    //   startTime: "",
    //   endTime: "",
    // }]);
  };

  const handleAddWeeklyWorkDay = async () => {
    try {
      const weeklyWorkDay: WeeklyWorkDayDto = {
        workPlan: { id: workPlanId },
        dayOfWeek: formValues.dayOfWeek,
        startTime: formValues.startTime,
        endTime: formValues.endTime,
        employeeId: user.id,
        isBreak: formValues.isBreak ?? false,
      };

      // If plan has historical data, trigger versioning dialog
      if (hasHistoricalData && onVersioningRequired) {
        onVersioningRequired({ type: "create", weeklyWorkDay, clientId: createPendingId() });
        setIsAddingRow(false);
        setFormValues({ dayOfWeek: 1, startTime: "", endTime: "" });
        return;
      }

      await createWeeklyWorkDayMutation.mutateAsync(weeklyWorkDay);
      setIsAddingRow(false);
      setFormValues({ dayOfWeek: 1, startTime: "", endTime: "", isBreak: false });
      // React Query automatically invalidates and refetches the data
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteRow = (weeklyWorkDay: WeeklyWorkDayDto) => {
    setWeeklyWorkDayToDelete(weeklyWorkDay);
  };

  const handleEditClick = (id: number, weeklyWorkDay: WeeklyWorkDayDto) => {
    setEditingRowId(id);
    // console.log("weeklyWorkDay to edit", weeklyWorkDay);
    const values = {
      dayOfWeek: weeklyWorkDay.dayOfWeek!,
      startTime: weeklyWorkDay.startTime!,
      endTime: weeklyWorkDay.endTime!,
    };
    setFormValues(values);
  };

  const handleCancelClick = () => {
    // Reset editing state
    setIsAddingRow(false);
    setEditingRowId(null);
    setFormValues({ dayOfWeek: 1, startTime: "", endTime: "", isBreak: false });
  };

  const handleCloseDialog = () => {
    setWeeklyWorkDayToDelete(null);
  };

  const handleConfirmDelete = async (id: number) => {
    try {
      await deleteWeeklyWorkDayMutation.mutateAsync({
        weeklyWorkDayId: id,
        userId: user.id,
        workPlanId
      });
      setIsAddingRow(false);
      if (selectedWeeklyWorkDay?.id === id) {
        setSelectedWeeklyWorkDay(undefined);
      }
      // React Query automatically invalidates and refetches the data
    } catch (error) {
      // Blocked deletes (§ 4 ArbZG) are expected: the global mutation error
      // handler already shows the German explanation — swallowing here just
      // prevents an unhandled promise rejection (dev error overlay).
      console.error("Error deleting weekly work day:", error);
    } finally {
      setWeeklyWorkDayToDelete(null);
    }
  };

  const handleUpdateWeeklyWorkDay = async () => {
    try {
      const today = new Date();
      const startTimeHour = parseInt(formValues.startTime.split(":")[0]);
      const startTimeMinute = parseInt(formValues.startTime.split(":")[1]);
      const endTimeHour = parseInt(formValues.endTime.split(":")[0]);
      const endTimeMinute = parseInt(formValues.endTime.split(":")[1]);
      const startTime = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        startTimeHour,
        startTimeMinute
      );
      const endTime = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        endTimeHour,
        endTimeMinute
      );

      // Find the original row to check if it's a break
      const originalRow = weeklyWorkDays.find((w) => w.id === editingRowId);
      const isBreak = originalRow?.isBreak ?? false;

      const updatedWeeklyWorkDay: WeeklyWorkDayDto = {
        id: editingRowId!,
        workPlan: { id: workPlanId },
        dayOfWeek: formValues.dayOfWeek,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        employeeId: user.id,
        isBreak,
      };

      console.log("handleUpdateWeeklyWorkDay:", {
        hasHistoricalData,
        hasOnVersioningRequired: !!onVersioningRequired,
        isBreak,
      });

      // If plan has historical data AND it's not a break, trigger versioning dialog
      // Breaks use the exception system instead of versioning
      if (hasHistoricalData && onVersioningRequired && !isBreak) {
        console.log("Triggering versioning dialog!");
        onVersioningRequired({ type: "update", weeklyWorkDay: updatedWeeklyWorkDay });
        setEditingRowId(null);
        setFormValues({ dayOfWeek: 1, startTime: "", endTime: "" });
        return;
      }

      // console.log("updatedWeeklyWorkDay", updatedWeeklyWorkDay);

      await updateWeeklyWorkDayMutation.mutateAsync(updatedWeeklyWorkDay);
      setEditingRowId(null);
      setFormValues({ dayOfWeek: 1, startTime: "", endTime: "" });
      // React Query automatically invalidates and refetches the data
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        {/* Left Card - Work Days Table */}
        <Grid item xs={12} md={8}>
          <FlatCard title="Arbeitstag">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {workdayColumns.map((column) => (
                      <TableCell key={column.id}>{column.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
              {weeklyWorkDays.map((workDay: MergedWeeklyWorkDay, index: number) => (
                <TableRow
                  key={workDay.displayId}
                  hover
                  sx={{
                    cursor: "pointer",
                    // A pending row is tinted like the block in the calendar,
                    // so the same change is recognisable in both places.
                    ...(workDay.isPending && {
                      backgroundColor: alpha(PENDING_COLOR, 0.1),
                    }),
                  }}
                  onClick={() => handleRowClick(workDay)}
                  selected={workDay.id != null && selectedWeeklyWorkDay?.id === workDay.id}
                >
                  {/* A pending new row has no id yet — it gets one when the
                      new plan version is created. */}
                  <TableCell>{workDay.id ?? "neu"}</TableCell>
                  {editingRowId === workDay.id ? (
                    <>
                      <TableCell>
                        <Controls.Select
                          label="Wochentag"
                          value={daysOfWeek.find((day) => day.id === workDay.dayOfWeek?.toString())
                            ?.value}
                          onChange={(e: any) =>
                            setFormValues({ ...formValues, dayOfWeek: e.target.id })
                          }
                          showOptionLabel
                          options={daysOfWeek}
                          style={{ width: "130px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TimePicker
                          ampm={false}
                          label="ab"
                          minTime={new Date(0, 0, 0, 7)}
                          maxTime={new Date(0, 0, 0, 20)}
                          defaultValue={new Date().setHours(
                            parseInt(workDay.startTime?.split(":")[0]!),
                            parseInt(workDay.startTime?.split(":")[1]!)
                          )}
                          slotProps={{ textField: { size: "small" } }}
                          onChange={(date: any) =>
                            setFormValues({
                              ...formValues,
                              startTime:
                                date.getHours().toString().padStart(2, "0") +
                                ":" +
                                date.getMinutes().toString().padStart(2, "0"),
                            })
                          }
                          sx={{ width: "120px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TimePicker
                          ampm={false}
                          label="bis"
                          minTime={new Date(0, 0, 0, 7)}
                          maxTime={new Date(0, 0, 0, 20)}
                          defaultValue={new Date().setHours(
                            parseInt(workDay.endTime?.split(":")[0]!),
                            parseInt(workDay.endTime?.split(":")[1]!)
                          )}
                          slotProps={{ textField: { size: "small" } }}
                          onChange={(date: any) =>
                            setFormValues({
                              ...formValues,
                              endTime:
                                date.getHours().toString().padStart(2, "0") +
                                ":" +
                                date.getMinutes().toString().padStart(2, "0"),
                            })
                          }
                          sx={{ width: "120px" }}
                        />
                      </TableCell>
                      {/* Typ is not editable here, but the cell has to exist:
                          without it every cell after "bis" slid one column left
                          and the buttons overflowed the table. */}
                      <TableCell>{workDay.isBreak ? "Pause" : "Arbeitszeit"}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        {
                          daysOfWeek.find((day) => day.id === workDay.dayOfWeek?.toString())
                            ?.label
                        }
                      </TableCell>
                      <TableCell>{workDay.startTime}</TableCell>
                      <TableCell>{workDay.endTime}</TableCell>
                      <TableCell>
                        {workDay.isBreak ? "Pause" : "Arbeitszeit"}
                        {workDay.isPending && (
                          <Chip
                            label="ausstehend"
                            size="small"
                            variant="outlined"
                            sx={{
                              ml: 1,
                              height: 20,
                              fontSize: "0.7rem",
                              color: PENDING_COLOR,
                              borderColor: alpha(PENDING_COLOR, 0.5),
                            }}
                          />
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    {/* One row of actions that never wraps: a wrapping Grid
                        stacked the buttons and doubled every row's height. */}
                    {editingRowId === workDay.id ? (
                      <Stack direction="row" spacing={1} sx={{ whiteSpace: "nowrap" }}>
                        <Button size="small" startIcon={<Save />} onClick={handleUpdateWeeklyWorkDay}>
                          Speichern
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Clear />}
                          onClick={handleCancelClick}
                        >
                          Abbrechen
                        </Button>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        {/* A row that has never been saved cannot be edited or
                            deleted through the API — it has no id yet. It is
                            withdrawn with VERWERFEN in the banner. */}
                        <Button
                          size="small"
                          sx={{ minWidth: 0 }}
                          disabled={readOnly || workDay.id == null}
                          onClick={() => handleEditClick(workDay.id!, workDay)}
                        >
                          <Edit />
                        </Button>
                        <Button
                          size="small"
                          sx={{ minWidth: 0 }}
                          disabled={readOnly || workDay.id == null}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRow(workDay);
                          }}
                        >
                          <Delete />
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {/* New Row for Adding */}
              {isAddingRow && (
                <TableRow>
                  <TableCell>*</TableCell>
                  <TableCell>
                    <Controls.Select
                      label="Wochentag"
                      value={formValues.dayOfWeek}
                      onChange={(e: any) =>
                        setFormValues({ ...formValues, dayOfWeek: e.target.id })
                      }
                      showOptionLabel
                      options={daysOfWeek}
                      style={{ width: "150px" }} // Adjust the width as needed
                    />
                  </TableCell>
                  <TableCell>
                    <TimePicker
                      ampm={false}
                      label="ab"
                      minTime={new Date(0, 0, 0, 7)}
                      maxTime={new Date(0, 0, 0, 20)}
                      onChange={(date: any) => setFormValues({ ...formValues, startTime: date })}
                      sx={{ width: "150px" }}
                    />
                  </TableCell>
                  <TableCell>
                    <TimePicker
                      ampm={false}
                      label="bis"
                      minTime={new Date(0, 0, 0, 7)}
                      maxTime={new Date(0, 0, 0, 20)}
                      onChange={(date: any) => setFormValues({ ...formValues, endTime: date })}
                      sx={{ width: "150px" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Controls.Select
                      label="Typ"
                      value={formValues.isBreak ? "Pause" : "Arbeitszeit"}
                      onChange={(e: any) =>
                        setFormValues({ ...formValues, isBreak: e.target.value === "Pause" })
                      }
                      showOptionLabel
                      options={[
                        { label: "Arbeitszeit", value: "Arbeitszeit", id: "work" },
                        { label: "Pause", value: "Pause", id: "break" },
                      ]}
                      style={{ width: "150px" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Grid container spacing={2}>
                      <Grid item>
                        <Button onClick={handleAddWeeklyWorkDay}>
                          <Save />
                          Speichern
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button variant="outlined" onClick={handleCancelClick}>
                          <Clear />
                          Abbrechen
                        </Button>
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Button for Adding New Row */}
        {!isAddingRow && !readOnly && (
          <Button onClick={addNewRow} sx={{ mt: 2 }}>
            <Add />
            Neu
          </Button>
        )}
      </FlatCard>
        </Grid>

        {/* Right Card - Weekly Hours Summary */}
        <Grid item xs={12} md={4}>
          {weeklyWorkDays.length > 0 && (
            <FlatCard title="Wochenstunden Zusammenfassung">
              <Box sx={{ p: 2 }}>
                {/* Say which week these hours describe. They now include the
                    pending changes, and a total that moves without the user
                    having saved anything needs to explain itself. */}
                {hasPendingChanges && (
                  <>
                    <Typography variant="body2" sx={{ color: PENDING_COLOR }}>
                      Inklusive der ausstehenden Änderungen — gespeichert werden sie erst mit
                      ANWENDEN.
                    </Typography>
                    {/* The statutory breaks are added by the API when the new
                        version is created, so "Pausen" and "Netto" can still
                        move after applying. Saying it here keeps that from
                        reading as the same defect all over again. */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                      Gesetzlich vorgeschriebene Pausen ergänzt das System beim Anwenden — "Pausen
                      gesamt" kann danach höher liegen.
                    </Typography>
                  </>
                )}
                <Grid container spacing={3} direction="column">
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      Arbeitszeit gesamt:
                    </Typography>
                    <Typography variant="h5" color="black" fontWeight="medium">
                      {formatHours(weeklyHoursSummary.totalHours)} Std.
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      Pausen gesamt:
                    </Typography>
                    <Typography variant="h5" color="info.main" fontWeight="medium">
                      {formatHours(weeklyHoursSummary.breakHours)} Std.
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      Netto Arbeitszeit:
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatHours(weeklyHoursSummary.netHours)} Std.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </FlatCard>
          )}
        </Grid>
      </Grid>
      <DeleteConfirmationDialog
        open={weeklyWorkDayToDelete !== null}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        id={weeklyWorkDayToDelete?.id!}
      />
    </>
  );
};

export default WeeklyWorkDays;
